// Rename a whole trip, from the trip's own page.
//
// A trip is not a row: the Trip Name lives on each of its legs (see
// TripGroups.mjs), so renaming one writes EVERY leg. That is why this is a
// confirm-shaped dialog rather than the logbook's rename-as-you-type box
// (Logbook.jsx): there is nothing sensible to autosave per keystroke across N
// rows.
//
// `onRename(trimmedName)` performs the rename and throws on failure -- errors
// are shown inline here because the page's status bar is behind the modal, the
// same arrangement ImportTripModal uses. On success the parent closes this.
//
// `otherTripNames` are the item's OTHER named trips, and `merges` says what
// sharing one of those names would do. That used to be a single answer -- the
// name WAS the trip, so a colliding rename merged the two -- and now it depends
// on whether this trip has an identity (TripIdentity.mjs):
//
//   merges: false  a trip with an id. The two stay separate, which is the whole
//                  point of ids; the warning is downgraded to a heads-up that
//                  the list will show the name twice.
//   merges: true   a legacy name-keyed trip, where it is still literally true.
//
// Either way it never blocks, for the reason it never did: the gazetteer editor
// applies the same rule to a colliding place rename, because curating names is
// the workflow rather than a mistake to be caught. A name list that failed to
// load is simply absent (null), which costs a warning, never the rename.

import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { extractErrorText } from '../../fleet-shared/Common.mjs';

// The other trip whose name this one would join, if any. Case-insensitive,
// because grouping is (tripGroupKey lowercases).
function collidingName(value, otherTripNames) {
  const wanted = value.trim().toLowerCase();
  if (wanted === '') return null;
  return (otherTripNames ?? []).find(
    (name) => String(name ?? '').trim().toLowerCase() === wanted
  ) ?? null;
}

const RenameTripModal = ({ show, currentName, otherTripNames, merges = true, onHide, onRename }) => {
  const [name, setName] = useState(currentName ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // This dialog stays mounted between opens (only `show` flips), so each open
  // has to start from the trip's current name rather than the last edit.
  useEffect(() => {
    if (!show) return;
    setName(currentName ?? '');
    setError(null);
    setBusy(false);
    // Deliberately keyed on `show` alone: the page's own fetch can land while
    // this is open, and a re-seed then would overwrite what the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const trimmed = name.trim();
  const collision = collidingName(name, otherTripNames);

  async function submit(event) {
    event?.preventDefault();
    // A blank name would give every leg no name at all, and an unnamed leg
    // stands alone as its own trip -- the rename would silently scatter the
    // trip into single legs. The button is disabled too; this covers Enter.
    if (trimmed === '' || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onRename(trimmed);
      setBusy(false);
    } catch (err) {
      setError(extractErrorText(err));
      setBusy(false);
    }
  }

  function handleHide() {
    if (busy) return;
    setError(null);
    onHide();
  }

  return (
    <Modal show={show} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>Rename Trip</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          <Form.Label htmlFor="rename-trip-name">Trip name</Form.Label>
          <Form.Control
            id="rename-trip-name"
            type="text"
            value={name}
            autoFocus
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
          <Form.Text>Every leg of this trip takes the new name.</Form.Text>

          {collision && (
            <Alert variant={merges ? 'warning' : 'info'} className="py-2 mb-0 mt-3">
              {merges ? (
                <>
                  Another trip is already called "{collision}". Renaming will
                  merge the two into one trip.
                </>
              ) : (
                <>
                  Another trip is also called "{collision}". They stay separate
                  trips.
                </>
              )}
            </Alert>
          )}

          {error && (
            <p className="text-danger small mb-0 mt-3" role="alert">{error}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleHide} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || trimmed === ''}>
            {busy ? 'Renaming...' : 'Rename'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RenameTripModal;
